import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Button, ClickAwayListener, Paper, Tooltip } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  BoardButton as BoardButtonModel,
  BoardButtonStyle,
} from "../../types/app/boardButton.types";
import { dictionary } from "../../utils/core/dictionary";
import { pathFullscreen, paths } from "../../utils/core/routes";

type MuiButtonColor =
  | "inherit"
  | "primary"
  | "secondary"
  | "success"
  | "error"
  | "info"
  | "warning";

const muiColorFromStyle = (style: BoardButtonStyle): MuiButtonColor => {
  switch (style) {
    case "Orange":
      return "primary";
    case "Gray":
      return "secondary";
    case "Green":
      return "success";
    case "Amber":
      return "warning";
    case "Red":
      return "error";
    case "Blue":
      return "info";
    default:
      return "inherit";
  }
};

// Pokud URL míří na jiný board v této aplikaci, přepíše ji tak,
// aby cílový board otevřela ve stejném režimu (fullscreen / normal),
// v jakém je aktuální pohled. Jinak vrací URL beze změny.
const resolveButtonUrl = (rawUrl: string, isFullScreen: boolean): string => {
  if (!rawUrl) return rawUrl;
  let parsed: URL;
  let isRelative = false;
  try {
    parsed = new URL(rawUrl);
  } catch {
    try {
      parsed = new URL(rawUrl, window.location.origin);
      isRelative = true;
    } catch {
      return rawUrl;
    }
  }
  if (!isRelative && parsed.origin !== window.location.origin) {
    return rawUrl;
  }
  const normalPrefix = paths.BOARD_DETAIL;
  const fsPrefix = pathFullscreen.BOARD_DETAIL;
  const path = parsed.pathname;

  let suffix: string | null = null;
  if (path === fsPrefix || path.startsWith(`${fsPrefix}/`)) {
    suffix = path.slice(fsPrefix.length);
  } else if (path === normalPrefix || path.startsWith(`${normalPrefix}/`)) {
    suffix = path.slice(normalPrefix.length);
  }
  if (suffix === null) return rawUrl;

  parsed.pathname = (isFullScreen ? fsPrefix : normalPrefix) + suffix;
  return isRelative
    ? `${parsed.pathname}${parsed.search}${parsed.hash}`
    : parsed.toString();
};

// Lišta se ovládá primárně prstem na dotykové obrazovce, ne myší — proto
// nikde neklesáme pod doporučenou velikost dotykového cíle.
const TOUCH_TARGET_MIN = 44;

// Šířka tlačítka, které rozbaluje skryté položky. Musí odpovídat rezervaci
// v rootMargin observeru níž, jinak by se poslední tlačítko schovalo pod něj.
const OVERFLOW_TRIGGER_WIDTH = 72;

type Density = "comfortable" | "compact" | "dense";

const densityForCount = (count: number): Density => {
  if (count <= 6) return "comfortable";
  if (count <= 11) return "compact";
  return "dense";
};

// S rostoucím počtem tlačítek se ubírá jen vodorovné místo a velikost písma.
// Výška zůstává na TOUCH_TARGET_MIN — nižší tlačítko se prstem špatně trefuje.
const densityStyles: Record<Density, { fontSize: string; paddingX: number }> = {
  comfortable: { fontSize: "0.875rem", paddingX: 2 },
  compact: { fontSize: "0.8125rem", paddingX: 1.5 },
  dense: { fontSize: "0.75rem", paddingX: 1.25 },
};

interface Props {
  buttons: BoardButtonModel[];
  isFullScreen: boolean;
}

export function BoardButtonBar({ buttons, isFullScreen }: Props) {
  const barRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map());
  const [clippedIds, setClippedIds] = useState<number[]>([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const buttonIdsKey = buttons.map((button) => button.id).join(",");

  useEffect(() => {
    const root = barRef.current;
    if (!root) return;

    // Které tlačítko se do lišty nevejde, spočítá prohlížeč sám. Odpadá tím
    // ruční měření šířek i přepočet při změně velikosti okna, zoomu nebo
    // doběhnutí webfontu — observer si to ohlídá.
    const observer = new IntersectionObserver(
      (entries) => {
        setClippedIds((previous) => {
          const next = new Set(previous);
          entries.forEach((entry) => {
            const id = Number((entry.target as HTMLElement).dataset.buttonId);
            if (Number.isNaN(id)) return;
            // Subpixelové zaokrouhlení — proti přesné 1 by to falešně skrývalo.
            if (entry.intersectionRatio < 0.99) next.add(id);
            else next.delete(id);
          });
          const sorted = Array.from(next).sort((a, b) => a - b);
          const isUnchanged =
            sorted.length === previous.length &&
            sorted.every((id, index) => id === previous[index]);
          return isUnchanged ? previous : sorted;
        });
      },
      {
        root,
        threshold: [0, 1],
        // Místo pro rozbalovací tlačítko ukrajujeme vždy, i když se zrovna
        // všechno vejde. Kdyby se rezervace zapínala až při přetečení, lišta
        // by mezi oběma stavy oscilovala.
        rootMargin: `0px -${OVERFLOW_TRIGGER_WIDTH}px 0px 0px`,
      }
    );

    itemRefs.current.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [buttonIdsKey]);

  const clippedIdSet = useMemo(() => new Set(clippedIds), [clippedIds]);
  const hiddenButtons = buttons.filter((button) => clippedIdSet.has(button.id));
  const hiddenCount = hiddenButtons.length;

  useEffect(() => {
    if (hiddenCount === 0) setIsPanelOpen(false);
  }, [hiddenCount]);

  const registerItemRef = (id: number) => (element: HTMLElement | null) => {
    if (element) itemRefs.current.set(id, element);
    else itemRefs.current.delete(id);
  };

  const handleButtonClick = (button: BoardButtonModel) => {
    setIsPanelOpen(false);
    const url = resolveButtonUrl(button.url, isFullScreen);
    if (button.openInNewTab) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = url;
    }
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    // Klepnutí na rozbalovací tlačítko si obslouží ono samo. Bez téhle výjimky
    // by ClickAwayListener panel zavřel a onClick by ho hned zase otevřel.
    if (triggerRef.current?.contains(event.target as Node)) return;
    setIsPanelOpen(false);
  };

  const { fontSize, paddingX } = densityStyles[densityForCount(buttons.length)];

  const renderButton = (button: BoardButtonModel, isClipped: boolean) => (
    <Tooltip key={button.id} title={button.tooltip ?? ""}>
      <Button
        onClick={() => handleButtonClick(button)}
        variant="contained"
        color={muiColorFromStyle(button.style)}
        tabIndex={isClipped ? -1 : undefined}
        sx={{
          minHeight: TOUCH_TARGET_MIN,
          px: paddingX,
          fontSize,
          whiteSpace: "nowrap",
        }}
      >
        {button.label}
      </Button>
    </Tooltip>
  );

  if (buttons.length === 0) return null;

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: "grey.100",
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Box
        ref={barRef}
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          overflow: "hidden",
          alignItems: "center",
          gap: 1,
          px: 1,
          py: 0.75,
        }}
      >
        {buttons.map((button) => {
          const isClipped = clippedIdSet.has(button.id);
          return (
            <Box
              key={button.id}
              component="span"
              data-button-id={button.id}
              ref={registerItemRef(button.id)}
              aria-hidden={isClipped}
              sx={{
                flex: "0 0 auto",
                // Skryté tlačítko zůstává v layoutu. Kdyby se odmountovalo,
                // zbytek by se do lišty rázem vešel, observer by ho vrátil
                // zpátky a celé by to začalo blikat dokola.
                visibility: isClipped ? "hidden" : "visible",
                pointerEvents: isClipped ? "none" : "auto",
              }}
            >
              {renderButton(button, isClipped)}
            </Box>
          );
        })}
      </Box>

      {hiddenCount > 0 && (
        <Box
          ref={triggerRef}
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            height: "100%",
            width: OVERFLOW_TRIGGER_WIDTH,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "grey.100",
            borderLeft: 1,
            borderColor: "divider",
          }}
        >
          <Button
            onClick={() => setIsPanelOpen((isOpen) => !isOpen)}
            color="inherit"
            aria-expanded={isPanelOpen}
            sx={{
              minWidth: 0,
              minHeight: TOUCH_TARGET_MIN,
              px: 1,
              fontSize: "0.8125rem",
              fontWeight: 700,
            }}
            endIcon={
              <KeyboardArrowDownIcon
                sx={{
                  transform: isPanelOpen ? "rotate(180deg)" : "none",
                  transition: "transform 120ms",
                }}
              />
            }
          >
            {`+${hiddenCount}`}
          </Button>
        </Box>
      )}

      {isPanelOpen && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            elevation={8}
            sx={{
              // Panel překrývá obsah boardu, nezasouvá ho. Přeskládat board
              // uprostřed meetingu je rušivější než překryv, který za chvíli
              // zase zmizí.
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: (theme) => theme.zIndex.appBar + 1,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              p: 1.5,
            }}
          >
            {hiddenButtons.map((button) => renderButton(button, false))}
            <Button
              onClick={() => setIsPanelOpen(false)}
              color="inherit"
              startIcon={<CloseIcon />}
              sx={{ ml: "auto", minHeight: TOUCH_TARGET_MIN }}
            >
              {dictionary.CLOSE}
            </Button>
          </Paper>
        </ClickAwayListener>
      )}
    </Box>
  );
}
